'use client';

import { c as _c } from "react/compiler-runtime";
import { useQuery } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { UnauthQueryOptions } from '@/api';
import { SearchSideAdBanner } from '@/app/_components/ads';
import { DesktopPageShell } from '@/components/template/DesktopPageShell';
import { NATIONWIDE_FESTIVAL_RADIUS_METER } from '@/constants/festival';
import { DEFAULT_LOCATION } from '@/constants/geo';
import { GTM_EVENT_TYPE, trackGtmEvent } from '@/utils/analytics/gtm';
import { EMPTY_AI_SUMMARY_MESSAGE, PAGE_SIZE, PREVIEW_SIZE } from '../../constants';
import { formatApplicationPeriod } from '../../utils';
import type { PolicyData } from '../PolicySearch/SearchPolicyCard';
import { SearchEmptyResults } from '../SearchEmptyResults';
import { BestContentsPreviewSection } from './BestContentsPreviewSection';
import { DonationBusinessPreviewSection } from './DonationBusinessPreviewSection';
import { FestivalPreviewSection } from './FestivalPreviewSection';
import { GiftPreviewSection } from './GiftPreviewSection';
import { HometownNewsPreviewSection } from './HometownNewsPreviewSection';
import { LocalMarketPreviewSection } from './LocalMarketPreviewSection';
import { PolicyPreviewSection } from './PolicyPreviewSection';
const DONATION_PREVIEW_SIZE = 3;
interface AllSearchProps {
  keyword: string;
}
export function AllSearch(t0) {
  const $ = _c(48);
  const {
    keyword
  } = t0;
  let t1;
  if ($[0] !== keyword) {
    t1 = UnauthQueryOptions["get:/wello/v2/allows/query/integrated"]({
      query: {
        searchKeyword: keyword || undefined,
        policySort: "SCORE",
        festivalSubRegionCode: DEFAULT_LOCATION.codeSubRegion,
        festivalRadius: NATIONWIDE_FESTIVAL_RADIUS_METER,
        page: 1,
        size: PAGE_SIZE
      }
    });
    $[0] = keyword;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t1) {
    t2 = {
      ...t1,
      select: _temp
    };
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const integratedQuery = useQuery(t2);
  const blocks = integratedQuery.data;
  let t3;
  if ($[4] !== blocks?.policy?.contents) {
    t3 = (blocks?.policy?.contents ?? []).slice(0, PREVIEW_SIZE).map(_temp2);
    $[4] = blocks?.policy?.contents;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const policyItems = t3;
  const policyTotal = blocks?.policy?.totalCount ?? 0;
  let t4;
  if ($[6] !== blocks?.hometownNews?.contents) {
    t4 = blocks?.hometownNews?.contents ?? [];
    $[6] = blocks?.hometownNews?.contents;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== t4) {
    t5 = t4.slice(0, PREVIEW_SIZE);
    $[8] = t4;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const hometownNewsItems = t5;
  let t6;
  if ($[10] !== blocks?.festival?.content) {
    t6 = blocks?.festival?.content ?? [];
    $[10] = blocks?.festival?.content;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  const festivalItems = t6;
  let t7;
  if ($[12] !== blocks?.bestContents?.contents) {
    t7 = blocks?.bestContents?.contents ?? [];
    $[12] = blocks?.bestContents?.contents;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== t7) {
    t8 = t7.slice(0, PREVIEW_SIZE);
    $[14] = t7;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  const bestContentsItems = t8;
  let t9;
  if ($[16] !== blocks?.giftProduct?.contents) {
    t9 = blocks?.giftProduct?.contents ?? [];
    $[16] = blocks?.giftProduct?.contents;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  const giftItems = t9;
  let t10;
  if ($[18] !== blocks?.donationBusiness?.contents) {
    t10 = blocks?.donationBusiness?.contents ?? [];
    $[18] = blocks?.donationBusiness?.contents;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  let t11;
  if ($[20] !== t10) {
    t11 = t10.slice(0, DONATION_PREVIEW_SIZE);
    $[20] = t10;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  const donationItems = t11;
  let t12;
  if ($[22] !== blocks?.localMarket?.contents) {
    t12 = blocks?.localMarket?.contents ?? [];
    $[22] = blocks?.localMarket?.contents;
    $[23] = t12;
  } else {
    t12 = $[23];
  }
  const localMarketItems = t12;
  const isAnyPending = integratedQuery.isPending;
  const isAllEmpty = !isAnyPending && policyItems.length < 1 && hometownNewsItems.length < 1 && festivalItems.length < 1 && bestContentsItems.length < 1 && giftItems.length < 1 && donationItems.length < 1 && localMarketItems.length < 1;
  const lastTrackedKeyword = useRef(null);
  let t13;
  let t14;
  if ($[24] !== isAllEmpty || $[25] !== isAnyPending || $[26] !== keyword) {
    t13 = () => {
      if (!keyword || isAnyPending) {
        return;
      }
      if (lastTrackedKeyword.current === keyword) {
        return;
      }
      lastTrackedKeyword.current = keyword;
      trackGtmEvent(GTM_EVENT_TYPE.SEARCH, {
        search_type: "\uD1B5\uD569\uAC80\uC0C9",
        search_result: isAllEmpty ? "N" : "Y",
        search_term: keyword,
        search_category: "all"
      });
    };
    t14 = [keyword, isAnyPending, isAllEmpty];
    $[24] = isAllEmpty;
    $[25] = isAnyPending;
    $[26] = keyword;
    $[27] = t13;
    $[28] = t14;
  } else {
    t13 = $[27];
    t14 = $[28];
  }
  useEffect(t13, t14);
  let t15;
  if ($[29] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <SearchSideAdBanner />;
    $[29] = t15;
  } else {
    t15 = $[29];
  }
  let t16;
  if ($[30] !== bestContentsItems || $[31] !== blocks?.bestContents?.totalCount || $[32] !== blocks?.donationBusiness?.totalCount || $[33] !== blocks?.festival?.totalElements || $[34] !== blocks?.giftProduct?.totalCount || $[35] !== blocks?.hometownNews?.totalCount || $[36] !== blocks?.localMarket?.totalCount || $[37] !== donationItems || $[38] !== festivalItems || $[39] !== giftItems || $[40] !== hometownNewsItems || $[41] !== isAllEmpty || $[42] !== isAnyPending || $[43] !== keyword || $[44] !== localMarketItems || $[45] !== policyItems || $[46] !== policyTotal) {
    t16 = <DesktopPageShell className="mt-20 w-full mobile-tablet:mt-0" rightAside={t15}>{isAllEmpty ? <SearchEmptyResults /> : <div className="flex flex-col gap-40 pb-40 mobile-tablet:pb-24"><BestContentsPreviewSection keyword={keyword} items={bestContentsItems} totalCount={blocks?.bestContents?.totalCount ?? 0} isLoading={isAnyPending} /><PolicyPreviewSection keyword={keyword} items={policyItems} totalCount={policyTotal} isLoading={isAnyPending} /><HometownNewsPreviewSection keyword={keyword} items={hometownNewsItems} totalCount={blocks?.hometownNews?.totalCount ?? 0} isLoading={isAnyPending} /><FestivalPreviewSection keyword={keyword} items={festivalItems} totalCount={blocks?.festival?.totalElements ?? 0} isLoading={isAnyPending} /><GiftPreviewSection keyword={keyword} items={giftItems} totalCount={blocks?.giftProduct?.totalCount ?? 0} isLoading={isAnyPending} /><DonationBusinessPreviewSection keyword={keyword} items={donationItems} totalCount={blocks?.donationBusiness?.totalCount ?? 0} isLoading={isAnyPending} /><LocalMarketPreviewSection keyword={keyword} items={localMarketItems} totalCount={blocks?.localMarket?.totalCount ?? 0} isLoading={isAnyPending} /></div>}</DesktopPageShell>;
    $[30] = bestContentsItems;
    $[31] = blocks?.bestContents?.totalCount;
    $[32] = blocks?.donationBusiness?.totalCount;
    $[33] = blocks?.festival?.totalElements;
    $[34] = blocks?.giftProduct?.totalCount;
    $[35] = blocks?.hometownNews?.totalCount;
    $[36] = blocks?.localMarket?.totalCount;
    $[37] = donationItems;
    $[38] = festivalItems;
    $[39] = giftItems;
    $[40] = hometownNewsItems;
    $[41] = isAllEmpty;
    $[42] = isAnyPending;
    $[43] = keyword;
    $[44] = localMarketItems;
    $[45] = policyItems;
    $[46] = policyTotal;
    $[47] = t16;
  } else {
    t16 = $[47];
  }
  return t16;
}
function _temp2(policy) {
  return {
    id: policy.metaPolicyIdIdx ?? String(policy.id ?? ""),
    slugId: policy.slugId,
    contentId: policy.id,
    title: policy.policyName ?? "",
    aiSummary: policy.aiSummary ?? EMPTY_AI_SUMMARY_MESSAGE,
    applicationPeriod: formatApplicationPeriod(policy.dueDateStart, policy.dueDateEnd),
    agencyName: policy.agency ?? "",
    agencyLogo: policy.agencyLogo ?? "",
    isBookmarked: Boolean(policy.wishlistYN)
  };
}
function _temp(data) {
  return data.context;
}